import { useEffect, useRef } from 'react';
import toast from 'react-hot-toast';

interface ReloadTracker {
    reloads: number[];
    lastNotificationTime: number;
}

const useRateLimitNotification = () => {
    const reloadTrackerRef = useRef<ReloadTracker>({
        reloads: [],
        lastNotificationTime: 0
    });

    const showToast = (type: 'warning' | 'error' | 'info' | 'success', title: string, message: string) => {

        const beautifulToastOptions = {
            duration: type === 'error' ? 6000 : 5000,
            position: 'top-right' as const,
            style: {
                borderRadius: '16px',
                fontSize: '15px',
                fontWeight: '500',
                padding: '16px 20px',
                maxWidth: '380px',
                minWidth: '300px',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(16px)',
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                textAlign: 'left' as const,
                lineHeight: '1.5'
            },
        };

        try {
            switch (type) {
                case 'warning':
                    toast(`${title}\n${message}`, {
                        ...beautifulToastOptions,
                        icon: '⚠️',
                        style: {
                            ...beautifulToastOptions.style,
                            background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                            color: '#ffffff',
                            border: '1px solid rgba(245, 158, 11, 0.3)',
                            boxShadow: '0 20px 25px -5px rgba(245, 158, 11, 0.2), 0 10px 10px -5px rgba(245, 158, 11, 0.1)'
                        }
                    });

                    break;
                case 'error':
                    toast.error(`🚫 ${title}\n${message}`, {
                        ...beautifulToastOptions,
                        style: {
                            ...beautifulToastOptions.style,
                            background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                            color: '#ffffff',
                            border: '1px solid rgba(239, 68, 68, 0.3)',
                            boxShadow: '0 20px 25px -5px rgba(239, 68, 68, 0.2), 0 10px 10px -5px rgba(239, 68, 68, 0.1)'
                        }
                    });

                    break;
                case 'info':
                    toast(`ℹ️ ${title}\n${message}`, {
                        ...beautifulToastOptions,
                        icon: 'ℹ️',
                        style: {
                            ...beautifulToastOptions.style,
                            background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                            color: '#ffffff',
                            border: '1px solid rgba(59, 130, 246, 0.3)',
                            boxShadow: '0 20px 25px -5px rgba(59, 130, 246, 0.2), 0 10px 10px -5px rgba(59, 130, 246, 0.1)'
                        }
                    });

                    break;
                case 'success':
                    toast(`${title}\n${message}`, {
                        ...beautifulToastOptions,
                        icon: '✅',
                        duration: 4000,
                        style: {
                            ...beautifulToastOptions.style,
                            background: 'linear-gradient(135deg, #10b981, #059669)',
                            color: '#ffffff',
                            border: '1px solid rgba(16, 185, 129, 0.3)',
                            boxShadow: '0 20px 25px -5px rgba(16, 185, 129, 0.3), 0 10px 10px -5px rgba(16, 185, 129, 0.2)'
                        }
                    });

                    break;
            }
        } catch (error) {
            console.error('🚨 Toast error:', error);
        }
    };

    const trackPageReload = () => {
        const now = Date.now();
        const tracker = reloadTrackerRef.current;

        // Add current reload timestamp
        tracker.reloads.push(now);

        // Keep only reloads from last 2 minutes
        const twoMinutesAgo = now - (2 * 60 * 1000);
        tracker.reloads = tracker.reloads.filter(time => time > twoMinutesAgo);

        // Check for frequent reloads (more than 4 reloads in 2 minutes)
        if (tracker.reloads.length >= 4) {
            const timeSinceLastNotification = now - tracker.lastNotificationTime;

            // Only notify once every 60 seconds
            if (timeSinceLastNotification > 60000) {
                showToast('warning', 'รีเฟรชบ่อยเกินไป', 'เพื่อป้องกันปัญหา กรุณาหลีกเลี่ยงการรีเฟรชติดต่อกัน');
                tracker.lastNotificationTime = now;
            }
        }

        if (process.env.NODE_ENV === 'development') {
            // Silent tracking
        }
    };

    useEffect(() => {
        // Track this page load as a reload
        trackPageReload();

        // Initialized silently

        // Listen for API request manager events
        const handleApiManagerEvent = (event: CustomEvent) => {
            const { type, details } = event.detail;

            switch (type) {
                case 'rate_limited':
                    const retryAfter = Math.ceil((details.retryDelay || 8000) / 1000);

                    showToast('warning', 'กำลังโหลดช้า', `กรุณารอสักครู่ แล้วลองใหม่ (รอ ${retryAfter}วินาที)`);
                    break;

                case 'circuit_breaker_opened':
                    const waitTime = Math.ceil((details.waitTime || 30000) / 1000);

                    showToast('error', 'โหลดหน้าเว็บบ่อยเกินไป', `กรุณารอ ${waitTime} วินาที ก่อนรีเฟรชหน้าใหม่`);
                    break;

                case 'many_retries':
                    if (details.retryCount >= 2) {

                        showToast('info', 'กำลังพยายามเชื่อมต่อ', 'หากรีเฟรชบ่อยเกินไป อาจต้องรอนานขึ้น');
                    }
                    break;

                case 'rate_limit_resolved':

                    showToast('success', 'ใช้งานได้ปกติแล้ว', 'ระบบกลับมาทำงานปกติแล้ว');
                    break;
            }
        };

        // Listen for events from ApiRequestManager
        window.addEventListener('apiManagerEvent', handleApiManagerEvent as EventListener);

        return () => {
            window.removeEventListener('apiManagerEvent', handleApiManagerEvent as EventListener);
        };
    }, []);

    return {
        trackPageReload,
        showToast
    };
};

export default useRateLimitNotification;
